import React, { KeyboardEventHandler } from 'react';
import { isFunction } from 'lodash';

import { DropdownToggleInterface } from '@neptune/shared/venus-domain';

import { DropdownItem } from '../dropdown-item/DropdownItem';
import { DropdownGroup } from '../dropdown-menu/DropdownGroup';
import { DropdownMenu } from '../dropdown-menu/DropdownMenu';
import { DropdownScrollArea } from '../dropdown-menu/DropdownScrollArea';
import {
  DropdownMenuAnimation,
  DropdownMenuAnimationConfiguration,
} from '../dropdown-menu-animation/DropdownMenuAnimation';
import { DropdownSeparator } from '../dropdown-separator/DropdownSeparator';
import { DropdownSubmenuToggle } from '../dropdown-submenu-toggle/DropdownSubmenuToggle';
import {
  DropdownTether,
  DropdownTetherProps,
  HackedTether,
  IOnOutsideClick,
} from '../dropdown-tether/DropdownTether';
import { DropdownTitle } from '../dropdown-title/DropdownTitle';
import { DropdownToggle } from '../dropdown-toggle/DropdownToggle';

export type DropdownChildrenRendererParams = {
  toggle: () => void;
  collapse: () => void;
  expand: () => void;
};

export type DropdownChildrenRenderer = (params: DropdownChildrenRendererParams) => React.ReactNode;

export type DropdownProps = Partial<DropdownTetherProps> & {
  disabled?: boolean;
  onExpand?: (closeFunction: () => void) => void;
  onCollapse?: () => void;
  onOutsideClick?: IOnOutsideClick;
  onKeyDown?: (event: React.KeyboardEvent, collapse: () => void) => void;
  onKeyUp?: (event: React.KeyboardEvent, collapse: () => void) => void;
  toggle: React.ReactElement<DropdownToggleInterface> | string;
  children?: React.ReactNode | DropdownChildrenRenderer;
  menuAnimation?: DropdownMenuAnimationConfiguration;
  elementRef?: React.MutableRefObject<HackedTether | null>;
};

type DropdownState = {
  expanded: boolean;
  pendingExitAnimation: boolean;
};

/** @deprecated use ActionsMenu from @neptune/shared/venus-ui instead */
export class Dropdown extends React.PureComponent<DropdownProps, DropdownState> {
  public static Toggle = DropdownToggle;
  public static SubmenuToggle = DropdownSubmenuToggle;
  public static Menu = DropdownMenu;
  public static Tether = DropdownTether;
  public static Item = DropdownItem;
  public static Group = DropdownGroup;
  public static Separator = DropdownSeparator;
  public static Title = DropdownTitle;
  public static ScrollArea = DropdownScrollArea;

  public static CONSTRAINT_TO_WINDOW: React.ComponentProps<typeof Dropdown>['constraints'] = [
    { to: 'window', attachment: 'together', pin: true },
  ];

  public static CONSTRAINT_TO_WINDOW_NO_PIN: React.ComponentProps<typeof Dropdown>['constraints'] =
    [{ to: 'window', attachment: 'together', pin: false }];

  public static SUBMENU_ATTACHMENTS: Pick<
    React.ComponentProps<typeof Dropdown>,
    'attachment' | 'targetAttachment'
  > = {
    attachment: 'top left',
    targetAttachment: 'top right',
  };

  public static DROPDOWN_ANIMATION_DOWN: React.ComponentProps<typeof Dropdown>['menuAnimation'] = {
    direction: 'top-bottom',
  };

  public static DROPDOWN_ANIMATION_UP: React.ComponentProps<typeof Dropdown>['menuAnimation'] = {
    direction: 'bottom-top',
  };

  state = {
    expanded: false,
    pendingExitAnimation: false,
  };

  componentDidUpdate(_: DropdownProps, prevState: DropdownState) {
    if (prevState.expanded === false && this.state.expanded) {
      if (isFunction(this.props.onExpand)) {
        this.props.onExpand(() => this.collapse);
      }
    }

    if (prevState.expanded === true && !this.state.expanded) {
      if (isFunction(this.props.onCollapse)) {
        this.props.onCollapse();
      }
    }
  }

  handleMenuExited = () => {
    this.setState({ pendingExitAnimation: false });
  };

  collapse = () => {
    this.setState((prev) => ({
      expanded: false,
      pendingExitAnimation: prev.expanded && !!this.props.menuAnimation,
    }));
  };

  expand = () => {
    this.setState({
      expanded: true,
      pendingExitAnimation: false,
    });
  };

  toggle = () => {
    this.setState((prev) => ({
      expanded: !prev.expanded,
      pendingExitAnimation: prev.expanded && !!this.props.menuAnimation,
    }));
  };

  closeOnEscape: KeyboardEventHandler = (event) => {
    if (!this.state.expanded) {
      return;
    }

    if (event.key === 'Escape') {
      event.stopPropagation();
      this.collapse();
    }
  };

  handleKeyDown: React.KeyboardEventHandler = this.props.onKeyDown
    ? (event) => this.props.onKeyDown?.(event, this.collapse)
    : this.closeOnEscape;

  handleKeyUp: React.KeyboardEventHandler = (event) => {
    this.props.onKeyUp?.(event, this.collapse);
  };

  outsideClickHandler = (ev: MouseEvent) => {
    if (this.props.onOutsideClick) {
      this.props.onOutsideClick(ev, this.collapse);
    } else {
      this.collapse();
    }
  };

  renderChildren() {
    const { children, menuAnimation } = this.props;

    if (!this.state.expanded && !this.state.pendingExitAnimation) {
      return null;
    }

    const getChildrenComponent = () => {
      if (isFunction(children)) {
        return children({
          toggle: this.toggle,
          collapse: this.collapse,
          expand: this.expand,
        });
      }

      return children;
    };

    if (menuAnimation) {
      return (
        <DropdownMenuAnimation
          onExited={this.handleMenuExited}
          menuAnimation={menuAnimation}
          in={this.state.expanded}
        >
          {getChildrenComponent()}
        </DropdownMenuAnimation>
      );
    }

    return getChildrenComponent();
  }

  handleToggleKeyDown = (event: React.KeyboardEvent) => {
    const { toggle } = this.props;

    if (typeof toggle === 'object') {
      toggle.props?.onKeyDown?.(event);
    }

    this.closeOnEscape(event);
  };

  render() {
    const { disabled, toggle, onOutsideClick, ...tetherProps } = this.props;

    const { expanded } = this.state;

    const toggleWithProps =
      typeof toggle === 'string' ? (
        <DropdownToggle
          pressed={expanded}
          disabled={disabled}
          label={toggle}
          onToggle={disabled ? undefined : this.toggle}
        />
      ) : (
        React.cloneElement(toggle, {
          pressed: expanded,
          disabled,
          onToggle: disabled ? undefined : this.toggle,
          onCollapse: this.collapse,
          onExpand: this.expand,
          onKeyDown: this.handleToggleKeyDown,
        })
      );

    return (
      <DropdownTether
        onOutsideClick={expanded ? this.outsideClickHandler : undefined}
        onElementKeyDown={this.handleKeyDown}
        onElementKeyUp={this.handleKeyUp}
        autoWidthMenu="none"
        {...tetherProps}
      >
        {toggleWithProps}
        {this.renderChildren()}
      </DropdownTether>
    );
  }
}
